import { Component, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AdminOrganization, AdminService } from '../../../core/services/admin.service';
import { BillingPlan } from '../../../core/services/billing.service';
import { LoggerService } from '../../../core/services/logger.service';
import { ToastService } from '../../../core/services/toast.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { SelectComponent } from '../../../shared/components/form-controls/select/select.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';

interface AdminRow {
  id: string;
  name: string;
  customerStatus: string;
  planId: string;
  status: string;
  originalPlanId: string;
  originalStatus: string;
  saving: boolean;
}

const SUBSCRIPTION_STATUSES = ['active', 'past_due', 'cancelled', 'trialing', 'unpaid'];

/**
 * Super-admin-only QA tool: grant/change any organization's subscription
 * plan+status directly, bypassing Stripe entirely (see backend/src/routes/admin.ts
 * and backend/scripts/grant-subscription.mts for the CLI equivalent).
 */
@Component({
  selector: 'app-admin-subscriptions',
  standalone: true,
  imports: [FormsModule, ButtonComponent, CardComponent, SelectComponent, SpinnerComponent],
  templateUrl: './admin-subscriptions.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-subscriptions.component.css',
})
export class AdminSubscriptionsComponent implements OnInit {
  private adminService = inject(AdminService);
  private toast = inject(ToastService);
  private logger = inject(LoggerService).getLogger('admin-subscriptions');

  state = signal<'loading' | 'loaded' | 'error'>('loading');
  rows = signal<AdminRow[]>([]);
  plans = signal<BillingPlan[]>([]);
  statuses = SUBSCRIPTION_STATUSES;

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.state.set('loading');
    this.adminService.getOrganizations().subscribe({
      next: (response) => {
        this.plans.set(response.plans);
        this.rows.set(
          response.organizations.map((org: AdminOrganization) => ({
            id: org.id,
            name: org.name,
            customerStatus: org.customerStatus,
            planId: org.plan?.id || 'free',
            status: org.subscriptionStatus || 'active',
            originalPlanId: org.plan?.id || 'free',
            originalStatus: org.subscriptionStatus || 'active',
            saving: false,
          })),
        );
        this.state.set('loaded');
      },
      error: (err) => {
        this.logger.error('Failed to load organizations:', err);
        this.state.set('error');
      },
    });
  }

  isDirty(row: AdminRow): boolean {
    return row.planId !== row.originalPlanId || row.status !== row.originalStatus;
  }

  save(row: AdminRow): void {
    this.rows.update((rows) => rows.map((r) => (r.id === row.id ? { ...r, saving: true } : r)));

    this.adminService
      .grantSubscription(row.id, { planId: row.planId, status: row.status })
      .subscribe({
        next: () => {
          this.toast.success(`${row.name} updated`);
          this.rows.update((rows) =>
            rows.map((r) =>
              r.id === row.id
                ? { ...r, saving: false, originalPlanId: r.planId, originalStatus: r.status }
                : r,
            ),
          );
        },
        error: (err) => {
          this.logger.error('Failed to grant subscription:', err);
          this.toast.error(`Failed to update ${row.name}`);
          this.rows.update((rows) =>
            rows.map((r) => (r.id === row.id ? { ...r, saving: false } : r)),
          );
        },
      });
  }
}
